import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as styles from './Navigation.css.ts';

const STORAGE_KEY = 'sidebar-collapsed';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  matchPaths?: string[];
  adminOnly?: boolean;
  search?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Operations',
    items: [
      { path: '/projects', label: 'Projects', icon: '📁' },
      { path: '/estimates', label: 'Estimates', icon: '📝' },
      ...(import.meta.env.VITE_ESTIMATOR_ENABLED === 'true'
        ? [
            { path: '/sow', label: 'Geometry', icon: '📐', search: '' },
            { path: '/sow', label: 'Estimator', icon: 'Σ', search: '?section=estimator' },
          ]
        : []),
      { path: '/orders', label: 'Orders', icon: '📦' },
      { path: '/receipts', label: 'Receipts', icon: '$' },
      { path: '/billing', label: 'Billing', icon: '🧾', adminOnly: true },
      { path: '/site-scout', label: 'Site Scout', icon: '📷' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { path: '/pipeline', label: 'Pipeline', icon: '📊', matchPaths: ['/pipeline', '/deals'] },
      { path: '/tasks', label: 'Tasks', icon: '✓' },
      { path: '/relationships', label: 'Relationships', icon: '🤝', matchPaths: ['/relationships', '/companies'] },
      { path: '/contacts', label: 'Contacts', icon: '👥' },
    ],
  },
  {
    label: 'Resources',
    items: [
      { path: '/inventory', label: 'Supplies', icon: '🏭' },
      { path: '/employees', label: 'Employees', icon: '👷' },
      { path: '/calendar', label: 'Calendar', icon: '📅' },
      { path: '/field-submissions', label: 'Field Submissions', icon: '🪧' },
    ],
  },
  {
    label: 'Docs',
    items: [
      { path: '/docs', label: 'Documents', icon: '📄' },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/dev-health', label: 'Dev Health', icon: '🩺', adminOnly: true },
    ],
  },
];

export default function Navigation() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const isActive = (item: NavItem) => {
    const paths = item.matchPaths || [item.path];
    const pathMatch = item.search === undefined
      ? paths.some(p => location.pathname.startsWith(p))
      : paths.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (!pathMatch) return false;
    if (item.search === undefined) return true;
    return location.search === item.search;
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const f = firstName?.[0] || '';
    const l = lastName?.[0] || '';
    return (f + l).toUpperCase() || '?';
  };

  if (!user) return null;

  return (
    <nav className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
      {/* Brand */}
      <div className={styles.brand}>
        <div className={styles.brandLogo}>ZE</div>
        <span className={`${styles.brandText} ${collapsed ? styles.brandTextHidden : ''}`}>
          Zeigler Environmental
        </span>
      </div>

      {/* Toggle button */}
      <button
        className={styles.toggleButton}
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? '›' : '‹'}
      </button>

      {/* Navigation links */}
      <div className={styles.navContent}>
        {NAV_SECTIONS.map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.adminOnly || user.role === 'admin'),
        }))
          .filter((section) => section.items.length > 0)
          .map((section) => (
          <div key={section.label} className={styles.navSection}>
            <div className={`${styles.sectionLabel} ${collapsed ? styles.sectionLabelHidden : ''}`}>
              {section.label}
            </div>
            {section.items.map((item) => (
              <Link
                key={item.label}
                to={item.search !== undefined ? { pathname: item.path, search: item.search } : item.path}
                className={`${styles.navLink} ${isActive(item) ? styles.navLinkActive : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <span className={styles.navLinkIcon}>{item.icon}</span>
                <span className={`${styles.navLinkText} ${collapsed ? styles.navLinkTextHidden : ''}`}>
                  {item.label}
                </span>
              </Link>
            ))}
          </div>
        ))}
      </div>

      {/* User section */}
      <div className={styles.userSection}>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            {getInitials(user.firstName, user.lastName)}
          </div>
          <span className={`${styles.userName} ${collapsed ? styles.userNameHidden : ''}`}>
            {[user.firstName, user.lastName].filter(Boolean).join(' ')}
          </span>
        </div>
        <button onClick={logout} className={styles.logoutButton} title={collapsed ? 'Logout' : undefined}>
          <span>🚪</span>
          <span className={`${styles.logoutText} ${collapsed ? styles.logoutTextHidden : ''}`}>
            Logout
          </span>
        </button>
      </div>
    </nav>
  );
}

// Export collapsed state hook for use in App layout
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      setCollapsed(stored === 'true');
    };

    // Listen for storage changes and custom event
    window.addEventListener('storage', handleStorage);
    window.addEventListener('sidebar-toggle', handleStorage);

    // Poll for changes (for same-tab updates)
    const interval = setInterval(() => {
      const stored = localStorage.getItem(STORAGE_KEY);
      setCollapsed(stored === 'true');
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('sidebar-toggle', handleStorage);
      clearInterval(interval);
    };
  }, []);

  return collapsed;
}
